import React, { createContext, ReactNode, useContext, useReducer } from 'react';
import { RcFile } from 'antd/lib/upload';
import { isEmpty } from 'lodash';

import { Nullable } from 'types';
import { Contract, ContractMode, ContractTerm } from 'pricing/types';

export interface ContractState {
  loading: boolean;
  contract: Nullable<Contract>;
  contractMode: ContractMode;
  termsFormVisible: boolean;
  editContractTerm: Nullable<ContractTerm>;
  amendmentFileUpload: Nullable<RcFile>;
  hasAmendmentUploaded: boolean;
}

export const contractInitState: ContractState = {
  loading: false,
  contract: null,
  contractMode: 'view',
  termsFormVisible: false,
  editContractTerm: null,
  amendmentFileUpload: null,
  hasAmendmentUploaded: false,
};

export type ContractAction =
  | { type: 'LOADING_CONTRACT' }
  | { type: 'SET_CONTRACT'; contract: Nullable<Contract> }
  | { type: 'SET_CONTRACT_MODE'; contractMode: ContractMode }
  | { type: 'SET_CONTRACT_TERM'; contractTerm: ContractTerm }
  | { type: 'SET_TERMS_FORM_VISIBLE'; termsFormVisible: boolean }
  | { type: 'SET_EDIT_TERM'; editContractTerm: Nullable<ContractTerm> }
  | { type: 'SET_AMENDMENT_FILE_UPLOAD'; amendmentFileUpload: Nullable<RcFile> }
  | { type: 'SET_HAS_AMENDMENT_UPLOADED'; hasAmendmentUploaded: boolean };

type Dispatch = (action: ContractAction) => void;

export const contractReducer = (
  state: ContractState,
  action: ContractAction,
): ContractState => {
  switch (action.type) {
    case 'LOADING_CONTRACT': {
      return { ...state, loading: true };
    }
    case 'SET_CONTRACT': {
      const { contract } = action;
      return {
        ...state,
        loading: false,
        contract,
      };
    }
    case 'SET_CONTRACT_MODE': {
      const { contractMode } = action;
      return {
        ...state,
        contractMode,
      };
    }
    case 'SET_CONTRACT_TERM': {
      const { contractTerm } = action;

      if (!state.contract) {
        return state;
      }

      const contractTerms = state.contract.breederContractTerms;

      if (!contractTerms || isEmpty(contractTerms)) {
        return {
          ...state,
          loading: false,
          contract: {
            ...state.contract,
            breederContractTerms: [contractTerm],
          },
        };
      }

      const termIndex = contractTerms.findIndex((term) => term.id === contractTerm.id);

      if (termIndex >= 0) {
        contractTerms[termIndex] = contractTerm;

        return {
          ...state,
          loading: false,
          contract: {
            ...state.contract,
            breederContractTerms: [...contractTerms],
          },
        };
      } else {
        return {
          ...state,
          loading: false,
          contract: {
            ...state.contract,
            breederContractTerms: [...contractTerms, contractTerm],
          },
        };
      }
    }
    case 'SET_TERMS_FORM_VISIBLE': {
      const { termsFormVisible } = action;
      return {
        ...state,
        termsFormVisible,
      };
    }
    case 'SET_EDIT_TERM': {
      const { editContractTerm } = action;
      return {
        ...state,
        editContractTerm: editContractTerm,
      };
    }
    case 'SET_AMENDMENT_FILE_UPLOAD': {
      const { amendmentFileUpload } = action;
      return {
        ...state,
        amendmentFileUpload,
      };
    }
    case 'SET_HAS_AMENDMENT_UPLOADED': {
      const { hasAmendmentUploaded } = action;
      return {
        ...state,
        hasAmendmentUploaded,
      };
    }

    default:
      return state;
  }
};

const ContractStateContext = createContext<
  { state: ContractState; dispatch: Dispatch } | undefined
>(undefined);

interface ContractProviderProps {
  children: ReactNode;
  reducerInitialState?: ContractState;
}

export function ContractProvider(props: ContractProviderProps) {
  const { children, reducerInitialState } = props;
  const initialState = reducerInitialState ?? contractInitState;

  const [state, dispatch] = useReducer(contractReducer, initialState);

  const value = { state, dispatch };

  return (
    <ContractStateContext.Provider value={value}>
      {children}
    </ContractStateContext.Provider>
  );
}

export function useContractState() {
  const context = useContext(ContractStateContext);
  if (!context)
    throw new Error('useContractState must be used within a ContractProvider');
  return context;
}
